import { useState, useCallback, useEffect, useRef } from "react";
import * as THREE from "three";
import { throttle } from "lodash";

const useScalpelMode = (
  sceneRef,
  cameraRef,
  containerRef,
  modelRef,
  controlsRef,
  selectedModels,
  undoStack,
  undoIndex,
  setProcessing,
  viewportRef = null
) => {
  const [isScalpelMode, setIsScalpelMode] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawPoints, setDrawPoints] = useState([]);
  const [cutHeight, setCutHeight] = useState(10);

  const MIN_DISTANCE = 0.05;

  const throttledMouseMove = useRef(null);
  const throttledTouchMove = useRef(null);

  // --------------------------
  // Utility functions
  // --------------------------

  // Get the (normalized) direction in which the camera is looking (WORLD space)
  const getCameraNormalVector = useCallback(() => {
    const cameraDirection = new THREE.Vector3();
    cameraRef.current.getWorldDirection(cameraDirection);
    return cameraDirection.normalize();
  }, [cameraRef]);

  const updateDrawingLine = useCallback(
    (points) => {
      if (points.length < 2) return;

      if (sceneRef.current.userData.currentDrawingLine) {
        sceneRef.current.remove(sceneRef.current.userData.currentDrawingLine);
        sceneRef.current.userData.currentDrawingLine.geometry.dispose();
        sceneRef.current.userData.currentDrawingLine.material.dispose();
      }

      try {
        if (points.length === 2) {
          const direction = new THREE.Vector3().subVectors(
            points[1],
            points[0]
          );
          const distance = direction.length();
          const midPoint = new THREE.Vector3()
            .addVectors(points[0], points[1])
            .multiplyScalar(0.5);

          const geometry = new THREE.CylinderGeometry(0.2, 0.2, distance, 8);
          const material = new THREE.MeshBasicMaterial({
            color: 0xff0000,
            transparent: true,
            opacity: 0.9,
          });

          const cylinder = new THREE.Mesh(geometry, material);
          cylinder.position.copy(midPoint);
          cylinder.lookAt(points[1]);
          cylinder.rotateX(Math.PI / 2);

          sceneRef.current.add(cylinder);
          sceneRef.current.userData.currentDrawingLine = cylinder;
        } else {
          const curve = new THREE.CatmullRomCurve3(
            points,
            false,
            "centripetal",
            0.1
          );

          const tubeGeometry = new THREE.TubeGeometry(
            curve,
            Math.min(64, points.length * 8),
            0.25,
            8,
            false
          );

          const tubeMaterial = new THREE.MeshBasicMaterial({
            color: 0xff0000,
            transparent: true,
            opacity: 0.9,
            side: THREE.DoubleSide,
          });

          const tubeMesh = new THREE.Mesh(tubeGeometry, tubeMaterial);
          sceneRef.current.add(tubeMesh);
          sceneRef.current.userData.currentDrawingLine = tubeMesh;
        }
      } catch (error) {
        const group = new THREE.Group();
        points.forEach((point) => {
          const sphereGeometry = new THREE.SphereGeometry(0.3, 8, 8);
          const sphereMaterial = new THREE.MeshBasicMaterial({
            color: 0xff0000,
            transparent: true,
            opacity: 0.8,
          });
          const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
          sphere.position.copy(point);
          group.add(sphere);
        });
        sceneRef.current.add(group);
        sceneRef.current.userData.currentDrawingLine = group;
      }
    },
    [sceneRef]
  );

  const pointInPolygon3D = useCallback(
    (point, polygonPoints, normal, height) => {
      if (polygonPoints.length < 3) return false;

      const center = new THREE.Vector3();
      polygonPoints.forEach((p) => center.add(p));
      center.divideScalar(polygonPoints.length);

      const toPoint = point.clone().sub(center);
      const distanceAlongNormal = toPoint.dot(normal);

      if (distanceAlongNormal > height) return false;

      const projectedPoint = point
        .clone()
        .sub(normal.clone().multiplyScalar(distanceAlongNormal));

      const u = new THREE.Vector3();
      const v = new THREE.Vector3();
      if (polygonPoints.length > 1) {
        u.subVectors(polygonPoints[1], polygonPoints[0]).normalize();
      } else {
        u.set(1, 0, 0);
      }

      v.crossVectors(normal, u).normalize();

      const projectedPolygon = polygonPoints.map((p) => {
        const relative = p.clone().sub(polygonPoints[0]);
        return new THREE.Vector2(relative.dot(u), relative.dot(v));
      });

      const relativePoint = projectedPoint.clone().sub(polygonPoints[0]);
      const point2D = new THREE.Vector2(
        relativePoint.dot(u),
        relativePoint.dot(v)
      );

      let inside = false;
      for (
        let i = 0, j = projectedPolygon.length - 1;
        i < projectedPolygon.length;
        j = i++
      ) {
        const xi = projectedPolygon[i].x,
          yi = projectedPolygon[i].y;
        const xj = projectedPolygon[j].x,
          yj = projectedPolygon[j].y;

        /* eslint-disable no-mixed-operators */
        if (
          yi > point2D.y !== yj > point2D.y &&
          point2D.x < ((xj - xi) * (point2D.y - yi)) / (yj - yi) + xi
        ) {
          inside = !inside;
        }
        /* eslint-enable no-mixed-operators */
      }

      return inside;
    },
    []
  );

  const interpolateOnEdge = useCallback(
    (v1, v2, n1, n2, polygonPoints, normal, height) => {
      const steps = 100;
      let lastInside = false;
      let intersectionPoint = null;
      let intersectionNormal = null;

      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const testPoint = new THREE.Vector3().lerpVectors(v1, v2, t);

        const isInside = pointInPolygon3D(
          testPoint,
          polygonPoints,
          normal,
          height
        );

        if (i > 0 && isInside !== lastInside) {
          const prevT = (i - 1) / steps;
          const finalT = (prevT + t) / 2;

          intersectionPoint = new THREE.Vector3().lerpVectors(v1, v2, finalT);
          intersectionNormal = new THREE.Vector3()
            .lerpVectors(n1, n2, finalT)
            .normalize();
          break;
        }

        lastInside = isInside;
      }

      return intersectionPoint
        ? { vertex: intersectionPoint, normal: intersectionNormal }
        : null;
    },
    [pointInPolygon3D]
  );

  const clipTriangleToPolygon = useCallback(
    (v1, v2, v3, n1, n2, n3, polygonPoints, normal, height) => {
      const inside = [
        pointInPolygon3D(v1, polygonPoints, normal, height),
        pointInPolygon3D(v2, polygonPoints, normal, height),
        pointInPolygon3D(v3, polygonPoints, normal, height),
      ];

      const insideCount = inside.filter(Boolean).length;

      if (insideCount === 0) {
        return [{ vertices: [v1, v2, v3], normals: [n1, n2, n3] }];
      }

      if (insideCount === 3) {
        return [];
      }

      const vertices = [v1, v2, v3];
      const normals = [n1, n2, n3];
      const edges = [
        [0, 1],
        [1, 2],
        [2, 0],
      ];

      const newVertices = [];
      const newNormals = [];

      for (let i = 0; i < 3; i++) {
        if (!inside[i]) {
          newVertices.push(vertices[i]);
          newNormals.push(normals[i]);
        }
      }

      const intersections = [];
      edges.forEach(([i, j]) => {
        if (inside[i] !== inside[j]) {
          const intersection = interpolateOnEdge(
            vertices[i],
            vertices[j],
            normals[i],
            normals[j],
            polygonPoints,
            normal,
            height
          );
          if (intersection) {
            intersections.push(intersection);
          }
        }
      });

      intersections.forEach((intersection) => {
        newVertices.push(intersection.vertex);
        newNormals.push(intersection.normal);
      });

      if (newVertices.length < 3) return [];
      if (newVertices.length === 3) {
        return [{ vertices: newVertices, normals: newNormals }];
      }

      const resultTriangles = [];
      for (let i = 1; i < newVertices.length - 1; i++) {
        resultTriangles.push({
          vertices: [newVertices[0], newVertices[i], newVertices[i + 1]],
          normals: [newNormals[0], newNormals[i], newNormals[i + 1]],
        });
      }

      return resultTriangles;
    },
    [pointInPolygon3D, interpolateOnEdge]
  );

  const processTriangleWithSubdivision = useCallback(
    (v1, v2, v3, n1, n2, n3, polygonPoints, normal, height, depth = 0) => {
      const maxDepth = 7;

      const edge1 = v1.distanceTo(v2);
      const edge2 = v2.distanceTo(v3);
      const edge3 = v3.distanceTo(v1);
      const maxEdge = Math.max(edge1, edge2, edge3);

      let polygonSize = 0;
      for (let i = 0; i < polygonPoints.length - 1; i++) {
        polygonSize += polygonPoints[i].distanceTo(polygonPoints[i + 1]);
      }
      polygonSize /= polygonPoints.length;

      if (depth >= maxDepth || maxEdge < polygonSize * 0.3) {
        return clipTriangleToPolygon(
          v1,
          v2,
          v3,
          n1,
          n2,
          n3,
          polygonPoints,
          normal,
          height
        );
      }

      const inside = [
        pointInPolygon3D(v1, polygonPoints, normal, height),
        pointInPolygon3D(v2, polygonPoints, normal, height),
        pointInPolygon3D(v3, polygonPoints, normal, height),
      ];

      const insideCount = inside.filter(Boolean).length;

      if (insideCount === 0 || insideCount === 3) {
        return clipTriangleToPolygon(
          v1,
          v2,
          v3,
          n1,
          n2,
          n3,
          polygonPoints,
          normal,
          height
        );
      }

      const mid12 = new THREE.Vector3().addVectors(v1, v2).multiplyScalar(0.5);
      const mid23 = new THREE.Vector3().addVectors(v2, v3).multiplyScalar(0.5);
      const mid31 = new THREE.Vector3().addVectors(v3, v1).multiplyScalar(0.5);

      const midN12 = new THREE.Vector3().addVectors(n1, n2).normalize();
      const midN23 = new THREE.Vector3().addVectors(n2, n3).normalize();
      const midN31 = new THREE.Vector3().addVectors(n3, n1).normalize();

      const subTriangles = [
        [v1, mid12, mid31, n1, midN12, midN31],
        [mid12, v2, mid23, midN12, n2, midN23],
        [mid31, mid23, v3, midN31, midN23, n3],
        [mid12, mid23, mid31, midN12, midN23, midN31],
      ];

      const results = [];
      subTriangles.forEach(([sv1, sv2, sv3, sn1, sn2, sn3]) => {
        results.push(
          ...processTriangleWithSubdivision(
            sv1,
            sv2,
            sv3,
            sn1,
            sn2,
            sn3,
            polygonPoints,
            normal,
            height,
            depth + 1
          )
        );
      });

      return results;
    },
    [clipTriangleToPolygon, pointInPolygon3D]
  );

  const removeTrianglesAndCollectSmooth = useCallback(
    (geometry, basePoints, normal, height) => {
      const positions = geometry.attributes.position.array;
      const normals = geometry.attributes.normal.array;
      const deletedTrianglesList = [];
      const newTriangles = [];

      const originalGeometry = {
        positions: Array.from(positions),
        normals: Array.from(normals),
      };

      // Ensure polygon is closed
      const polygonPoints = basePoints[0].equals(
        basePoints[basePoints.length - 1]
      )
        ? basePoints
        : [...basePoints, basePoints[0]];

      for (let i = 0; i < positions.length; i += 9) {
        const v1 = new THREE.Vector3(
          positions[i],
          positions[i + 1],
          positions[i + 2]
        );
        const v2 = new THREE.Vector3(
          positions[i + 3],
          positions[i + 4],
          positions[i + 5]
        );
        const v3 = new THREE.Vector3(
          positions[i + 6],
          positions[i + 7],
          positions[i + 8]
        );

        const n1 = new THREE.Vector3(
          normals[i],
          normals[i + 1],
          normals[i + 2]
        );
        const n2 = new THREE.Vector3(
          normals[i + 3],
          normals[i + 4],
          normals[i + 5]
        );
        const n3 = new THREE.Vector3(
          normals[i + 6],
          normals[i + 7],
          normals[i + 8]
        );

        const originalTriangle = {
          vertices: Array.from(positions.slice(i, i + 9)),
          normals: Array.from(normals.slice(i, i + 9)),
          triangleIndex: i / 9,
          startIndex: i,
        };

        const resultTriangles = processTriangleWithSubdivision(
          v1,
          v2,
          v3,
          n1,
          n2,
          n3,
          polygonPoints,
          normal,
          height
        );

        const wasModified =
          resultTriangles.length === 0 ||
          resultTriangles.length > 1 ||
          (resultTriangles.length === 1 &&
            (!resultTriangles[0].vertices[0].equals(v1) ||
              !resultTriangles[0].vertices[1].equals(v2) ||
              !resultTriangles[0].vertices[2].equals(v3)));

        if (wasModified) {
          deletedTrianglesList.push(originalTriangle);
        }

        newTriangles.push(...resultTriangles);
      }

      // Create new geometry from result triangles
      const newPositions = [];
      const newNormals = [];

      newTriangles.forEach((triangle) => {
        triangle.vertices.forEach((vertex, idx) => {
          newPositions.push(vertex.x, vertex.y, vertex.z);
          newNormals.push(
            triangle.normals[idx].x,
            triangle.normals[idx].y,
            triangle.normals[idx].z
          );
        });
      });

      const newGeometry = new THREE.BufferGeometry();
      newGeometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(newPositions, 3)
      );
      newGeometry.setAttribute(
        "normal",
        new THREE.Float32BufferAttribute(newNormals, 3)
      );

      console.log(
        `Cut operation: ${
          deletedTrianglesList.length
        } triangles affected out of ${positions.length / 9} total`
      );

      return { newGeometry, deletedTrianglesList, originalGeometry };
    },
    [processTriangleWithSubdivision]
  );

  const cutGeometryWithShape = useCallback(
    (geometry, basePoints, height, normal) => {
      return removeTrianglesAndCollectSmooth(
        geometry,
        basePoints,
        normal,
        height
      );
    },
    [removeTrianglesAndCollectSmooth]
  );

  const modifyGeometryAndAddToScene = useCallback(() => {
    console.log(`Applying smooth cut with height: ${cutHeight}`);

    const modelGroup = modelRef.current;
    const scene = sceneRef.current;
    if (!modelGroup || !scene) {
      setProcessing(false);
      console.error("Model or scene not found.");
      return;
    }
    if (!drawPoints || drawPoints.length < 3) {
      setProcessing(false);
      console.error(
        "Invalid drawPoints. Ensure there are at least 3 points to form a shape."
      );
      return;
    }

    // Ensure closed polygon (WORLD space points)
    const pointsWorld = drawPoints[0].equals(drawPoints[drawPoints.length - 1])
      ? drawPoints
      : [...drawPoints, drawPoints[0]];

    // Camera-facing normal (WORLD)
    const normalWorld = getCameraNormalVector();

    const scalpelUndo = {
      type: "scalpel",
      meshes: [],
      drawnShape: pointsWorld, // keep world for backward compatibility
      cutHeight: cutHeight,
      normal: normalWorld, // keep world for backward compatibility
      // NEW: per-mesh locals for deterministic replay
      pointsLocal: {}, // { [meshName]: THREE.Vector3[] }
      normalsLocal: {}, // { [meshName]: THREE.Vector3 }
      deletedTriangles: {},
      originalGeometries: {},
    };

    selectedModels.forEach((modelIndex) => {
      const mesh = modelGroup.children[modelIndex];
      if (!mesh) return;

      // Build LOCAL shape & LOCAL normal for this mesh
      const localPoints = pointsWorld.map((p) => mesh.worldToLocal(p.clone()));

      const normalMatrix = new THREE.Matrix3().getNormalMatrix(
        mesh.matrixWorld
      );
      const localNormal = normalWorld
        .clone()
        .applyMatrix3(normalMatrix)
        .normalize();

      // Cut USING locals (geometry is in local space)
      const { newGeometry, deletedTrianglesList, originalGeometry } =
        cutGeometryWithShape(
          mesh.geometry,
          localPoints,
          cutHeight,
          localNormal
        );

      if (deletedTrianglesList.length > 0) {
        scalpelUndo.meshes.push(mesh.name);
        scalpelUndo.deletedTriangles[mesh.name] = deletedTrianglesList;
        scalpelUndo.originalGeometries[mesh.name] = originalGeometry;
        scalpelUndo.pointsLocal[mesh.name] = localPoints;
        scalpelUndo.normalsLocal[mesh.name] = localNormal;
        console.log(
          `Smooth cut applied to ${mesh.name}, removed/modified ${deletedTrianglesList.length} triangles`
        );
      }

      mesh.geometry.dispose();
      mesh.geometry = newGeometry;
    });

    setDrawPoints([]);
    if (scene.userData.currentDrawingLine) {
      scene.remove(scene.userData.currentDrawingLine);
      scene.userData.currentDrawingLine.geometry.dispose();
      scene.userData.currentDrawingLine.material.dispose();
      scene.userData.currentDrawingLine = null;
    }

    // commit to history
    undoStack.current = undoStack.current.slice(0, undoIndex.current);
    undoStack.current.push(scalpelUndo);
    undoIndex.current += 1;

    setProcessing(false);
  }, [
    drawPoints,
    modelRef,
    sceneRef,
    selectedModels,
    cutHeight,
    cutGeometryWithShape,
    undoStack,
    undoIndex,
    setDrawPoints,
    setProcessing,
    getCameraNormalVector,
  ]);

  useEffect(() => {
    throttledMouseMove.current = throttle((event) => {
      if (!isDrawing || event.target.closest(".ui-outside-scene")) return;

      const rect = containerRef.current.getBoundingClientRect();
      
      // Use viewport dimensions if available, otherwise use container dimensions
      const viewport = viewportRef?.current || {
        left: 0,
        top: 0,
        width: rect.width,
        height: rect.height,
      };
      
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;
      
      const x = ((mouseX - viewport.left) / viewport.width) * 2 - 1;
      const y = -((mouseY - viewport.top) / viewport.height) * 2 + 1;
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera({ x, y }, cameraRef.current);
      const visibleModels = modelRef.current.children.filter(
        (child) => child.visible
      );
      if (visibleModels.length > 0) {
        const intersects = raycaster.intersectObjects(visibleModels, true);
        if (intersects.length > 0) {
          const intersectPoint = intersects[0].point;
          if (
            drawPoints.length === 0 ||
            drawPoints[drawPoints.length - 1].distanceTo(intersectPoint) >
              MIN_DISTANCE
          ) {
            setDrawPoints((prevPoints) => {
              const newPoints = [...prevPoints, intersectPoint];
              updateDrawingLine(newPoints);
              return newPoints;
            });
          }
        }
      }
    }, 16);

    throttledTouchMove.current = throttle((event) => {
      if (!isDrawing || event.target.closest(".ui-outside-scene")) return;
      event.preventDefault();
      const touch = event.touches[0];
      const rect = containerRef.current.getBoundingClientRect();
      
      // Use viewport dimensions if available, otherwise use container dimensions
      const viewport = viewportRef?.current || {
        left: 0,
        top: 0,
        width: rect.width,
        height: rect.height,
      };
      
      const mouseX = touch.clientX - rect.left;
      const mouseY = touch.clientY - rect.top;
      
      const x = ((mouseX - viewport.left) / viewport.width) * 2 - 1;
      const y = -((mouseY - viewport.top) / viewport.height) * 2 + 1;
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera({ x, y }, cameraRef.current);
      const visibleModels = modelRef.current.children.filter(
        (child) => child.visible
      );
      if (visibleModels.length > 0) {
        const intersects = raycaster.intersectObjects(visibleModels, true);
        if (intersects.length > 0) {
          const intersectPoint = intersects[0].point;
          if (
            drawPoints.length === 0 ||
            drawPoints[drawPoints.length - 1].distanceTo(intersectPoint) >
              MIN_DISTANCE
          ) {
            setDrawPoints((prevPoints) => {
              const newPoints = [...prevPoints, intersectPoint];
              updateDrawingLine(newPoints);
              return newPoints;
            });
          }
        }
      }
    }, 16);
  }, [
    isDrawing,
    cameraRef,
    containerRef,
    modelRef,
    sceneRef,
    drawPoints,
    updateDrawingLine,
    viewportRef,
  ]);

  const handleMouseMove = useCallback((event) => {
    if (throttledMouseMove.current) {
      throttledMouseMove.current(event);
    }
  }, []);

  const handleTouchMove = useCallback((event) => {
    if (throttledTouchMove.current) {
      throttledTouchMove.current(event);
    }
  }, []);

  const handleMouseDown = useCallback(
    (event) => {
      if (!isScalpelMode || event.target.closest(".ui-outside-scene")) return;
      if (event.button !== 0) return;
      const rect = containerRef.current.getBoundingClientRect();
      
      // Use viewport dimensions if available, otherwise use container dimensions
      const viewport = viewportRef?.current || {
        left: 0,
        top: 0,
        width: rect.width,
        height: rect.height,
      };
      
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;
      
      const x = ((mouseX - viewport.left) / viewport.width) * 2 - 1;
      const y = -((mouseY - viewport.top) / viewport.height) * 2 + 1;
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera({ x, y }, cameraRef.current);
      const visibleModels = modelRef.current.children.filter(
        (child) => child.visible
      );
      if (visibleModels.length > 0) {
        const intersects = raycaster.intersectObjects(visibleModels, true);
        if (intersects.length > 0) {
          const intersectPoint = intersects[0].point;
          setDrawPoints([intersectPoint]);
          setIsDrawing(true);
          controlsRef.current.noRotate = true;
        }
      }
    },
    [isScalpelMode, cameraRef, containerRef, modelRef, controlsRef, viewportRef]
  );

  const handleMouseUp = useCallback(
    (event) => {
      if (event.target.closest(".ui-outside-scene")) return;
      if (!isDrawing) return;
      setIsDrawing(false);
      controlsRef.current.noRotate = false;
    },
    [isDrawing, controlsRef]
  );

  const handleTouchStart = useCallback(
    (event) => {
      if (event.target.closest(".ui-outside-scene")) return;
      event.preventDefault();
      const touch = event.touches[0];
      const rect = containerRef.current.getBoundingClientRect();
      
      // Use viewport dimensions if available, otherwise use container dimensions
      const viewport = viewportRef?.current || {
        left: 0,
        top: 0,
        width: rect.width,
        height: rect.height,
      };
      
      const mouseX = touch.clientX - rect.left;
      const mouseY = touch.clientY - rect.top;
      
      const x = ((mouseX - viewport.left) / viewport.width) * 2 - 1;
      const y = -((mouseY - viewport.top) / viewport.height) * 2 + 1;
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera({ x, y }, cameraRef.current);
      const visibleModels = modelRef.current.children.filter(
        (child) => child.visible
      );
      if (visibleModels.length > 0) {
        const intersects = raycaster.intersectObjects(visibleModels, true);
        if (intersects.length > 0) {
          const intersectPoint = intersects[0].point;
          setDrawPoints([intersectPoint]);
          setIsDrawing(true);
          controlsRef.current.noRotate = true;
        }
      }
    },
    [containerRef, cameraRef, modelRef, controlsRef, viewportRef]
  );

  const handleTouchEnd = useCallback(
    (event) => {
      if (event.target.closest(".ui-outside-scene")) return;
      event.preventDefault();
      if (!isDrawing) return;
      setIsDrawing(false);
      controlsRef.current.noRotate = false;
    },
    [isDrawing, controlsRef]
  );

  const clearDrawPoints = useCallback(() => {
    setDrawPoints([]);
    if (sceneRef.current.userData.currentDrawingLine) {
      sceneRef.current.remove(sceneRef.current.userData.currentDrawingLine);
      sceneRef.current.userData.currentDrawingLine.geometry.dispose();
      sceneRef.current.userData.currentDrawingLine.material.dispose();
      sceneRef.current.userData.currentDrawingLine = null;
    }
  }, [sceneRef]);

  const undoScalpel = useCallback(
    (scalpelUndo) => {
      scalpelUndo.meshes.forEach((meshName) => {
        const originalGeometry = scalpelUndo.originalGeometries[meshName];

        if (!originalGeometry) {
          console.error(`Original geometry not found for mesh: ${meshName}`);
          return;
        }

        const mesh = modelRef.current.children.find(
          (child) => child.name === meshName
        );
        if (!mesh) {
          console.error(`Mesh ${meshName} not found for undoScalpel`);
          return;
        }

        const restoredGeometry = new THREE.BufferGeometry();
        restoredGeometry.setAttribute(
          "position",
          new THREE.Float32BufferAttribute(originalGeometry.positions, 3)
        );
        restoredGeometry.setAttribute(
          "normal",
          new THREE.Float32BufferAttribute(originalGeometry.normals, 3)
        );

        const oldGeometry = mesh.geometry;
        mesh.geometry = restoredGeometry;
        oldGeometry.dispose();

        console.log(`Fully restored original geometry for mesh: ${meshName}`);
      });
    },
    [modelRef]
  );

  const performScalpelAutoMultiple = useCallback(
    (scalpelData) => {
      const {
        meshes,
        drawnShape,
        cutHeight,
        normal,
        pointsLocal,
        normalsLocal,
      } = scalpelData;
      if (!meshes || cutHeight === undefined) {
        console.error(
          "Invalid scalpelData. Ensure that meshes and cutHeight are provided."
        );
        return;
      }

      meshes.forEach((meshName) => {
        const mesh = modelRef.current.children.find(
          (child) => child.name === meshName
        );
        if (!mesh) {
          console.error(`Mesh ${meshName} not found.`);
          return;
        }

        // Prefer per-mesh locals if we have them (new format)
        let localPts = pointsLocal?.[meshName];
        let localNrm = normalsLocal?.[meshName];

        // Fallback for older saved data: convert WORLD -> LOCAL
        if (!localPts) {
          if (!drawnShape || drawnShape.length < 3) {
            console.warn(
              `No pointsLocal or drawnShape for mesh ${meshName}; skipping.`
            );
            return;
          }
          localPts = drawnShape.map((p) =>
            mesh.worldToLocal(
              new THREE.Vector3(
                p.x ?? p._x ?? p,
                p.y ?? p._y ?? 0,
                p.z ?? p._z ?? 0
              )
            )
          );
        } else {
          // ensure actual THREE.Vector3 instances
          localPts = localPts.map((p) =>
            p.isVector3 ? p : new THREE.Vector3(p.x, p.y, p.z)
          );
        }

        if (!localNrm) {
          if (!normal) {
            console.warn(
              `No normalsLocal or normal for mesh ${meshName}; skipping.`
            );
            return;
          }
          const nWorld = new THREE.Vector3(normal.x, normal.y, normal.z);
          const normalMatrix = new THREE.Matrix3().getNormalMatrix(
            mesh.matrixWorld
          );
          localNrm = nWorld.clone().applyMatrix3(normalMatrix).normalize();
        } else if (!localNrm.isVector3) {
          localNrm = new THREE.Vector3(localNrm.x, localNrm.y, localNrm.z);
        }

        const { newGeometry } = cutGeometryWithShape(
          mesh.geometry,
          localPts,
          cutHeight,
          localNrm
        );
        mesh.geometry.dispose();
        mesh.geometry = newGeometry;

        console.log(`Smooth scalpel operation reapplied on mesh: ${meshName}`);
      });
    },
    [modelRef, cutGeometryWithShape]
  );

  useEffect(() => {
    if (isScalpelMode) {
      const currentContainer = containerRef.current;
      currentContainer.addEventListener("mousedown", handleMouseDown);
      currentContainer.addEventListener("mousemove", handleMouseMove);
      currentContainer.addEventListener("mouseup", handleMouseUp);
      currentContainer.addEventListener("touchstart", handleTouchStart, {
        passive: false,
      });
      currentContainer.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      currentContainer.addEventListener("touchend", handleTouchEnd);

      return () => {
        currentContainer.removeEventListener("mousedown", handleMouseDown);
        currentContainer.removeEventListener("mousemove", handleMouseMove);
        currentContainer.removeEventListener("mouseup", handleMouseUp);
        currentContainer.removeEventListener("touchstart", handleTouchStart);
        currentContainer.removeEventListener("touchmove", handleTouchMove);
        currentContainer.removeEventListener("touchend", handleTouchEnd);
        throttledMouseMove.current?.cancel();
        throttledTouchMove.current?.cancel();
      };
    }
  }, [
    isScalpelMode,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchMove,
    handleTouchStart,
    handleTouchEnd,
    containerRef,
  ]);

  return {
    isScalpelMode,
    setIsScalpelMode,
    setDrawPoints,
    cutHeight,
    setCutHeight,
    modifyGeometryAndAddToScene,
    clearDrawPoints,
    performScalpelAutoMultiple,
    undoScalpel,
  };
};

export default useScalpelMode;
